import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

const LOWEST_POPOVER_ZINDEX = 10000;

interface RegisteredPopoversContextState {
  /** The zIndex for each popover, indexed by their ID. */
  zIndexes: { [id: string]: number };
  /** If a new popover is opened, this is the zIndex it will get. */
  nextZIndex: number;
  /** Called when a popover transitions from closed to open. */
  openPopover(id: string): void;
  /** Called when a popover transitions from open to closed. */
  closePopover(id: string): void;
  /** Called to assign a new ID to a popover. */
  getNewPopoverId(): string;
}

const PopoverContext = React.createContext<RegisteredPopoversContextState>({
  zIndexes: {},
  nextZIndex: LOWEST_POPOVER_ZINDEX,
  openPopover() {
    throw new Error('Missing PopoverProvider in render tree.');
  },
  closePopover() {
    throw new Error('Missing PopoverProvider in render tree.');
  },
  getNewPopoverId() {
    throw new Error('Missing PopoverProvider in render tree.');
  },
});
PopoverContext.displayName = 'PopoverContext';

function getZIndexMap(popoverState: { id: string; zIndex: number }[]) {
  const zIndexes: { [id: string]: number } = {};
  popoverState.forEach((popover) => {
    zIndexes[popover.id] = popover.zIndex;
  });

  return zIndexes;
}

/**
 * Need one of these at the root of your render tree if you want to use
 * `usePopoverZIndex()`.
 */
export const PopoverZIndexProvider: React.FC<{ children: React.ReactNode }> = (props) => {
  // This is an array of all open popovers, in the order they were opened.
  const popoverStateRef = useRef<{ id: string; zIndex: number }[]>([]);
  const nextZIndex = useRef(LOWEST_POPOVER_ZINDEX);
  const [zIndexes, setZIndexes] = useState<{ [id: string]: number }>({});

  // Generate a new Popover ID when a popover registers itself.
  const nextId = useRef(1);
  function getNewPopoverId() {
    const id = nextId.current;
    nextId.current++;
    if (nextId.current === Number.MAX_SAFE_INTEGER) {
      // Note that if there's still a popover with ID 1 open, this is going to
      // cause a problem, but if create a new popover 10 times a second, it'll
      // be about 28.6 million years until we hit this case, so we're probably good.
      nextId.current = 1;
    }
    return `popover-${id}`;
  }

  const openPopover = useCallback((id: string) => {
    if (!popoverStateRef.current.find((popover) => popover.id === id)) {
      // Assign each popover a stable z-index to prevent jankiness if we close
      // a popover that's hidden somewhere in the middle of the stack.
      popoverStateRef.current = popoverStateRef.current.concat([
        { id, zIndex: nextZIndex.current },
      ]);
      nextZIndex.current++;
      setZIndexes(getZIndexMap(popoverStateRef.current));
    }
  }, []);

  const closePopover = useCallback((id: string) => {
    const newState = popoverStateRef.current.filter((popover) => popover.id !== id);
    if (newState.length !== popoverStateRef.current.length) {
      popoverStateRef.current = newState;
      nextZIndex.current =
        Math.max(LOWEST_POPOVER_ZINDEX - 1, ...popoverStateRef.current.map((s) => s.zIndex)) + 1;
      setZIndexes(getZIndexMap(popoverStateRef.current));
    }
  }, []);

  return (
    <PopoverContext.Provider
      value={{
        nextZIndex: nextZIndex.current,
        zIndexes,
        openPopover,
        closePopover,
        getNewPopoverId,
      }}
    >
      {props.children}
    </PopoverContext.Provider>
  );
};

/**
 * Returns the z-index to use for a popover or modal.
 *
 * The returned `zIndex` is assigned based on the order in which popovers are
 * opened - if popover B is opened while popover A is already open, then B
 * will have a higher zIndex than A, and the reverse is also true.
 *
 * @param isOpen - True if the popover/modal is currently open.
 */
export function usePopoverZIndex(isOpen: boolean) {
  const { zIndexes, openPopover, closePopover, getNewPopoverId, nextZIndex } =
    useContext(PopoverContext);
  const idRef = useRef(getNewPopoverId());

  useEffect(() => {
    const id = idRef.current;
    if (isOpen) {
      openPopover(id);
      // If `isOpen` changes, close the popover.  Note that `openPopover` and
      // `closePopver` will never change, so we don't have to worry about this
      // firing because of them.
      return () => closePopover(id);
    }
  }, [isOpen, openPopover, closePopover]);

  // If the modal is not open, return `nextZIndex` instead of undefined.  This is
  // the zIndex it will get if it opens, and it prevents us from having a single
  // janky frame where the modal has a zIndex of `undefined` right after it opens.
  return { zIndex: zIndexes[idRef.current] || nextZIndex };
}

/**
 * HOC version of `usePopover`.
 */
export function withPopoverZIndex<P extends { zIndex: number; isOpen: boolean }>(
  Component: React.ComponentType<P>,
) {
  return function WithPopoverZIndex(props: Omit<P, 'zIndex'>) {
    const { zIndex } = usePopoverZIndex(props.isOpen);
    return <Component {...(props as any)} zIndex={zIndex} />;
  };
}
